import { isNodeValueReference, isReferencedValue, valueReference } from "@skilitics/nautilus-core-designer-data";

import { getExternalValueDescriptor } from "./externalValues.ts";
import type { GetNodeValue } from "./querying/getNodeValue.ts";
import { subtypeSatisfies, type ValueSubtypeRequirement } from "./valueSubtypes.ts";

interface CreateOptions {
  getNodeValue: GetNodeValue;
}

/**
 * Whether a stored value config is a binding the consumer's subtype requirement
 * does not accept — an enum slot whose allowed list has since narrowed, say.
 *
 * Such a binding stays and complains rather than being cleared: unlike the
 * `stranded` case it is still renderable, and only the author knows what they
 * meant. A reference that resolves to nothing is the pre-existing dangling
 * failure mode, not a subtype mismatch, so it is not reported here.
 */
export const valueSubtypeMismatchCreate = ({ getNodeValue }: CreateOptions) => {
  return (configuredValue: unknown, required: ValueSubtypeRequirement | undefined): boolean => {
    if (!required || !isReferencedValue(configuredValue)) return false;
    const reference = valueReference(configuredValue);
    if (isNodeValueReference(reference)) {
      const resolved = getNodeValue(reference);
      return !!resolved && !subtypeSatisfies(required, resolved.descriptor.subtype);
    }
    const external = getExternalValueDescriptor(reference.property);
    return !!external && !subtypeSatisfies(required, external.subtype);
  };
};

export type ValueSubtypeMismatch = ReturnType<typeof valueSubtypeMismatchCreate>;
